import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SOURCE_LABELS, type ContactSourceFilter, type ContactStatusFilter } from "@/utils/contacts";

const STATUS_LABELS: Record<ContactStatusFilter, string> = {
  all: "Everyone",
  opted_in: "Subscribed",
  opted_out: "Opted out",
  unknown: "Not asked",
  invitable: "Invitable now",
  excluded: "No marketing texts",
};

const STATUS_OPTIONS: ContactStatusFilter[] = ["all", "opted_in", "opted_out", "unknown", "invitable", "excluded"];
const SOURCE_OPTIONS: ContactSourceFilter[] = [
  "all",
  "orders",
  "keyword",
  "invite",
  "click_to_join",
  "migration",
  "pos",
  "manual",
  "widget",
];

interface ContactsToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  status: ContactStatusFilter;
  onStatusChange: (value: ContactStatusFilter) => void;
  source: ContactSourceFilter;
  onSourceChange: (value: ContactSourceFilter) => void;
  caption: string;
}

/** Search + text club and source filters for the roster. State stays in the
 *  component, never the URL, so no name or number lands in browser history. */
export function ContactsToolbar({
  search,
  onSearchChange,
  status,
  onStatusChange,
  source,
  onSourceChange,
  caption,
}: ContactsToolbarProps) {
  return (
    <div className="grid gap-2 lg:grid-cols-[minmax(0,1fr)_12rem_12rem_auto] lg:items-center">
      <div className="relative">
        <Label htmlFor="contacts-search" className="sr-only">
          Search by name or phone
        </Label>
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          id="contacts-search"
          type="search"
          inputMode="search"
          autoComplete="off"
          placeholder="Search by name or phone"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-11 pl-9 text-base sm:text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-2 lg:contents">
        <Select value={status} onValueChange={(v) => onStatusChange(v as ContactStatusFilter)}>
          <SelectTrigger className="h-11" aria-label="Status">
            <SelectValue>{STATUS_LABELS[status]}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={(v) => onSourceChange(v as ContactSourceFilter)}>
          <SelectTrigger className="h-11" aria-label="Source">
            <SelectValue>{SOURCE_LABELS[source]}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {SOURCE_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {SOURCE_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <p className="text-xs text-muted-foreground lg:whitespace-nowrap" aria-live="polite">
        {caption}
      </p>
    </div>
  );
}
